import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Grid,
	MenuItem,
	Pagination,
	Select,
	SelectChangeEvent,
	Snackbar,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ISort } from 'features/common/types';
import {
	createSortHandler,
	ExportExcelOptions,
	exportToExcel,
	formatDateTimeToShowUser,
	getEnvelopedListPageTotal,
	getSortableFields,
	IHeadCell,
} from 'features/common/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ICashTransactionList, ICashTransactionLog } from 'features/transactions/types';
import {
	getCashTransactionList,
	getCashTransactionListToExport,
	getCashTransactionLogs,
} from 'features/transactions/services';
import { pageSizeOptions } from 'constants/constants';
import { ExportToExcelButton } from '../../../common/components/ExportToExcelButton';
import { CashTransactionLogsModal } from '../CashTransactionLogsModal';
import { MoveCashModal } from '../MoveCashModal';
import { PageNoResult } from '../../../common/components/PageNoResults';
import { CashTransactionFilters, CashTransactionFiltersValue } from '../CashTransactionFilters';

// ─── Columnas ─────────────────────────────────────────────────────────────────

const headerCells: IHeadCell[] = [
	{ field: 'id', sortable: true, headerName: '#' },
	{ field: 'crmTicket', sortable: true, headerName: 'Deal o Caso' },
	{ field: 'isBitrixDeal', sortable: true, headerName: 'Es un Deal' },
	{ field: 'coinCode', sortable: true, headerName: 'Moneda' },
	{ field: 'amount', sortable: true, headerName: 'Monto' },
	{ field: 'branchOfficeName', sortable: false, headerName: 'Sucursal' },
	{ field: 'bankAccountName', sortable: false, headerName: 'Cuenta bancaria' },
	{ field: 'amountInUSD', sortable: true, headerName: 'Monto en USD' },
	{ field: 'arbitration', sortable: true, headerName: 'Arbitraje' },
	{ field: 'observations', sortable: true, headerName: 'Observaciones' },
	{ field: 'createdAt', sortable: true, headerName: 'Fecha de creación' },
	{ field: 'createdBy', sortable: true, headerName: 'Creado por' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const transformValue = (key: string, value: unknown, row: ICashTransactionList): unknown => {
	switch (key) {
		case 'createdAt':
			return formatDateTimeToShowUser(row.createdAt);
		case 'observations':
			return row.observations ?? '—';
		case 'isBitrixDeal':
			return row.isBitrixDeal ? 'Sí' : 'No';
		case 'branchOfficeName':
			return row.branchOfficeName ?? '—';
		case 'bankAccountName':
			return row.bankAccountName ?? '—';
		default:
			return value;
	}
};

const filtersToRecord = (f: CashTransactionFiltersValue): Record<string, string | number | boolean> => {
	const out: Record<string, string | number | boolean> = {};
	if (f.branchOfficeId !== undefined) out.branchOfficeId = f.branchOfficeId;
	if (f.coinId !== undefined) out.coinId = String(f.coinId);
	if (f.crmTicket !== undefined) out.crmTicket = f.crmTicket;
	if (f.createdAtFrom) out.createdAtFrom = f.createdAtFrom;
	if (f.createdAtTo) out.createdAtTo = f.createdAtTo;
	if (f.createdBy) out.createdBy = f.createdBy;
	return out;
};

// ─── Componente ───────────────────────────────────────────────────────────────

export const CashTransactionList = (): JSX.Element => {
	// ── Estado de datos ───────────────────────────────────────────────────────
	const [cashTransactionList, setCashTransactionList] = useState<ICashTransactionList[]>([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSize, setCurrentSize] = useState(10);
	const [pageTotal, setPageTotal] = useState(0);
	const [sortFields, setSortFields] = useState<ISort[]>([{ field: 'createdAt', order: 'desc' }]);
	const [filters, setFilters] = useState<CashTransactionFiltersValue>({});

	// ── Estado de selección / movimiento ─────────────────────────────────────
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [openModalMoveCash, setOpenModalMoveCash] = useState(false);

	// ── Estado de logs ────────────────────────────────────────────────────────
	const [logsList, setLogsList] = useState<ICashTransactionLog[]>([]);
	const [openModalLogs, setOpenModalLogs] = useState(false);

	// ── Alertas ───────────────────────────────────────────────────────────────
	const [alert, setAlert] = useState<{ show: boolean; severity: 'success' | 'error' | 'info'; message: string }>({
		show: false,
		severity: 'success',
		message: '',
	});

	const showAlert = (severity: 'success' | 'error' | 'info', message: string) =>
		setAlert({ show: true, severity, message });

	// ── Carga principal ───────────────────────────────────────────────────────
	const loadList = useCallback(async () => {
		setLoading(true);
		setSelectedIds([]);
		try {
			const res = (await getCashTransactionList(currentPage - 1, currentSize, sortFields, filtersToRecord(filters)))
				.data;
			setPageTotal(getEnvelopedListPageTotal(res));
			setCashTransactionList([...res.data]);
		} catch (error) {
			showAlert('error', extractErrorMessage(error, 'Error al cargar las transacciones'));
		} finally {
			setLoading(false);
		}
	}, [currentPage, currentSize, sortFields, filters]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	// ── Handlers ─────────────────────────────────────────────────────────────
	const handleFilter = (newFilters: CashTransactionFiltersValue) => {
		setFilters(newFilters);
		setCurrentPage(1);
	};

	const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
		setCurrentSize(+event.target.value);
		setCurrentPage(1);
	};

	const sortableFields = getSortableFields(headerCells);

	const handleSelectRow = (id: number) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const handleClickViewLogs = async (id: number) => {
		try {
			setLoading(true);
			const logs = (await getCashTransactionLogs(id)).data;
			setLogsList(logs);
			if (logs.length === 0) {
				showAlert('info', 'La transacción no tiene movimientos relacionados');
			} else {
				setOpenModalLogs(true);
			}
		} catch (error) {
			showAlert('error', extractErrorMessage(error, 'Error al obtener los logs'));
		} finally {
			setLoading(false);
		}
	};

	const handleCloseMoveCashModal = (wasConfirmed: boolean) => {
		setOpenModalMoveCash(false);
		if (wasConfirmed) {
			loadList();
			showAlert('success', 'Movimiento realizado satisfactoriamente');
		}
	};

	const handleExportToExcel = async () => {
		try {
			setLoading(true);
			const res = (await getCashTransactionListToExport(sortFields, filtersToRecord(filters))).data;
			const options: ExportExcelOptions = {
				title: 'Listado de transacciones cash',
				headers: headerCells,
				widthColumns: [15, 20, 20, 20, 20, 30, 50, 20, 20, 50, 30, 50],
				filename: 'transacciones-cash',
			};
			exportToExcel(res.data, options, transformValue);
		} catch (error) {
			showAlert('error', extractErrorMessage(error, 'Error al exportar'));
		} finally {
			setLoading(false);
		}
	};

	// ── Totales de selección ─────────────────────────────────────────────────
	const selectionTotals: Record<string, number> | undefined = useMemo(() => {
		const rows = cashTransactionList.filter((r) => selectedIds.includes(r.id));
		if (rows.length === 0) return undefined;
		return rows.reduce<Record<string, number>>((acc, r) => {
			acc['Total USD'] = Number(((acc['Total USD'] ?? 0) + r.amountInUSD).toFixed(2));
			acc[r.coinCode] = Number(((acc[r.coinCode] ?? 0) + r.amount).toFixed(2));
			return acc;
		}, {});
	}, [selectedIds, cashTransactionList]);

	// ── Render ────────────────────────────────────────────────────────────────
	return (
		<>
			<CashTransactionFilters onFilter={handleFilter} />

			<Box display="flex" justifyContent="flex-end" gap={1} mb={1}>
				<ExportToExcelButton disabled={cashTransactionList.length === 0} handleExportToExcel={handleExportToExcel} />
				<Button
					onClick={() => setOpenModalMoveCash(true)}
					disabled={loading || selectedIds.length === 0}
					variant="contained"
					size="small"
					startIcon={<ArrowForwardIcon />}
					title="Mover cash"
				>
					Mover cash
				</Button>
			</Box>

			{loading && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}

			{!loading && (
				<TableContainer component="main" sx={{ maxWidth: 'xl' }}>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow sx={{ backgroundColor: 'primary.main' }}>
								<TableCell sx={{ backgroundColor: 'primary.main' }} />
								{headerCells.map((cell) =>
									cell.sortable ? (
										<TableCell
											key={cell.field}
											align="center"
											sx={{ backgroundColor: 'primary.main', color: 'white' }}
											sortDirection={sortFields.find((x) => x.field === cell.field)?.order ?? false}
										>
											<TableSortLabel
												active={!!sortFields.find((x) => x.field === cell.field)}
												sx={{ color: 'white !important', '&:hover': { color: 'white !important' } }}
												direction={sortFields.find((x) => x.field === cell.field)?.order ?? 'asc'}
												onClick={createSortHandler(cell.field, sortFields, sortableFields, setSortFields)}
											>
												{cell.headerName}
											</TableSortLabel>
										</TableCell>
									) : (
										<TableCell key={cell.field} align="center" sx={{ backgroundColor: 'primary.main', color: 'white' }}>
											{cell.headerName}
										</TableCell>
									),
								)}
								<TableCell align="center" sx={{ backgroundColor: 'primary.main', color: 'white' }}>
									Logs
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{cashTransactionList.length === 0 ? (
								<TableRow>
									<TableCell colSpan={headerCells.length + 2}>
										<PageNoResult />
									</TableCell>
								</TableRow>
							) : (
								cashTransactionList.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell padding="checkbox">
											{row.branchOfficeName != null && (
												<Checkbox
													checked={selectedIds.includes(row.id)}
													onClick={() => handleSelectRow(row.id)}
													size="small"
												/>
											)}
										</TableCell>
										<TableCell align="center">{row.id}</TableCell>
										<TableCell align="center">{row.crmTicket}</TableCell>
										<TableCell align="center">{row.isBitrixDeal ? 'Sí' : 'No'}</TableCell>
										<TableCell align="center">{row.coinCode}</TableCell>
										<TableCell align="center">{row.amount}</TableCell>
										<TableCell align="center">{row.branchOfficeName ?? '—'}</TableCell>
										<TableCell align="center">{row.bankAccountName ?? '—'}</TableCell>
										<TableCell align="center">{row.amountInUSD}</TableCell>
										<TableCell align="center">{row.arbitration}</TableCell>
										<TableCell align="center">{row.observations ?? '—'}</TableCell>
										<TableCell align="center">{formatDateTimeToShowUser(row.createdAt)}</TableCell>
										<TableCell align="center">{row.createdBy}</TableCell>
										<TableCell align="center">
											<Button
												title="Ver Logs"
												startIcon={<VisibilityIcon />}
												onClick={() => handleClickViewLogs(row.id)}
												size="small"
											/>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{/* ── Paginación ── */}
			<Grid container justifyContent="center" flexDirection="row" padding={2} alignItems="center">
				<Grid item xs={12} md={1} textAlign="center">
					<Select value={currentSize} size="small" onChange={handleRowsPerPageChange}>
						{pageSizeOptions.map((v) => (
							<MenuItem key={v} value={v}>
								{v}
							</MenuItem>
						))}
					</Select>
				</Grid>
				<Grid item xs={12} md={5} marginTop={1}>
					<Pagination count={pageTotal} page={currentPage} onChange={(_, p) => setCurrentPage(p)} color="primary" />
				</Grid>
				{selectionTotals && (
					<Grid item xs={12} md={4} marginTop={1}>
						<Table>
							<TableBody>
								<TableRow>
									{Object.entries(selectionTotals).map(([key, val], i) => (
										<TableCell key={i} padding="none" style={{ width: 50, border: 0 }}>
											<Typography component="p" fontWeight="bold">
												{key}
											</Typography>
											<Typography fontWeight="bold" variant="body2">
												{val}
											</Typography>
										</TableCell>
									))}
								</TableRow>
							</TableBody>
						</Table>
					</Grid>
				)}
			</Grid>

			{/* ── Modales ── */}
			{openModalLogs && (
				<CashTransactionLogsModal open={openModalLogs} data={logsList} onCancel={() => setOpenModalLogs(false)} />
			)}
			{openModalMoveCash && (
				<MoveCashModal open={openModalMoveCash} cashTransactionIds={selectedIds} onClose={handleCloseMoveCashModal} />
			)}

			<Snackbar
				open={alert.show}
				autoHideDuration={2000}
				onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
			>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
